import { Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { form, FormField, maxLength, required, requiredError, validate } from '@angular/forms/signals';
import { finalize } from 'rxjs';
import { LucideModule } from '../../../../../../shared/modules/lucide.module';
import {
  FORMATOS_ANEXO_COMPLEMENTO,
  LIMITE_ANEXOS_COMPLEMENTO,
  SolicitacaoPublicaResponse,
  TAMANHO_MAXIMO_COMPLEMENTO,
  TETO_ANEXO_COMPLEMENTO_BYTES,
} from '../../../../models/solicitacao-servico.model';
import {
  acceptDosFormatos,
  conferirArquivo,
  formatarTamanho,
} from '../../../../pages-servicos/solicitacao-servico.store';
import {
  ErroComplemento,
  mapearErroComplemento,
  SolicitacaoServicoPublicaService,
} from '../../../../services/solicitacao-servico-publica.service';
import { PendenciaSolicitacao } from '../../../../services/solicitacoes.service';

const MENSAGEM_TEXTO_OBRIGATORIO = 'Escreva o complemento.';

/** Um arquivo aceito, já com o tamanho legível. */
interface ArquivoComplemento {
  id: number;
  arquivo: File;
  tamanho: string;
}

/** Contador das instâncias, para os ids do card não colidirem entre a página e o modal. */
let sequencia = 0;

/**
 * O card "O órgão pediu um complemento" do acompanhamento por protocolo: a mensagem do órgão,
 * o prazo para enviar e o formulário de texto e anexos.
 *
 * O erro fica no próprio card, sem SweetAlert: o detalhe também é exibido dentro de um
 * `<dialog>`, que deixaria o alerta atrás e inerte. No sucesso, só emite a consulta atualizada;
 * quem troca os dados e avisa o cidadão é o detalhe, porque o card some junto com a pendência.
 */
@Component({
  selector: 'app-complemento-solicitacao',
  templateUrl: './complemento-solicitacao.component.html',
  styleUrls: ['./complemento-solicitacao.component.css'],
  imports: [LucideModule, FormField],
})
export class ComplementoSolicitacaoComponent {
  private readonly solicitacaoServicoPublica = inject(SolicitacaoServicoPublicaService);
  private readonly destroyRef = inject(DestroyRef);

  readonly prefixoId = `complemento-solicitacao-${++sequencia}`;

  readonly protocolo = input.required<string>();

  readonly pendencia = input.required<PendenciaSolicitacao>();

  readonly enviado = output<SolicitacaoPublicaResponse>();

  readonly limiteTexto = TAMANHO_MAXIMO_COMPLEMENTO;

  readonly limiteAnexos = LIMITE_ANEXOS_COMPLEMENTO;

  readonly accept = acceptDosFormatos(FORMATOS_ANEXO_COMPLEMENTO);

  readonly descricaoFormatos = FORMATOS_ANEXO_COMPLEMENTO.join(', ');

  readonly tetoAnexo = formatarTamanho(TETO_ANEXO_COMPLEMENTO_BYTES);

  readonly dados = signal({ texto: '' });

  readonly complementoForm = form(this.dados, (caminho) => {
    required(caminho.texto, { message: MENSAGEM_TEXTO_OBRIGATORIO });
    validate(caminho.texto, ({ value }) =>
      value() && !value().trim() ? requiredError({ message: MENSAGEM_TEXTO_OBRIGATORIO }) : null,
    );
    maxLength(caminho.texto, TAMANHO_MAXIMO_COMPLEMENTO, {
      message: `O complemento deve ter no máximo ${TAMANHO_MAXIMO_COMPLEMENTO} caracteres.`,
    });
  });

  readonly caracteres = computed(() => this.dados().texto.length);

  readonly arquivos = signal<readonly ArquivoComplemento[]>([]);

  readonly limiteAtingido = computed(() => this.arquivos().length >= LIMITE_ANEXOS_COMPLEMENTO);

  /** Os arquivos recusados na última seleção, com o motivo. A API nem chega a ser chamada. */
  readonly recusas = signal<readonly string[]>([]);

  readonly enviando = signal(false);

  readonly erro = signal<ErroComplemento | null>(null);

  private proximoId = 1;

  aoSelecionar(evento: Event): void {
    const campo = evento.target as HTMLInputElement;

    this.adicionar(campo.files);
    campo.value = '';
  }

  remover(id: number): void {
    this.arquivos.update((atuais) => atuais.filter((item) => item.id !== id));
    this.recusas.set([]);
  }

  /** O texto e os arquivos ficam no card quando o envio falha: o cidadão corrige e reenvia. */
  enviar(evento: Event): void {
    evento.preventDefault();

    if (this.enviando()) return;

    this.erro.set(null);

    if (!this.complementoForm().valid()) {
      this.complementoForm().markAsTouched();
      return;
    }

    this.enviando.set(true);

    this.solicitacaoServicoPublica
      .enviarComplemento(
        this.protocolo(),
        this.dados().texto,
        this.arquivos().map((item) => item.arquivo),
      )
      .pipe(
        finalize(() => this.enviando.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (resposta) => this.enviado.emit(resposta),
        error: (erro: unknown) => this.erro.set(mapearErroComplemento(erro)),
      });
  }

  private adicionar(lista: FileList | null): void {
    if (!lista?.length) return;

    const recusas: string[] = [];
    const aceitos: ArquivoComplemento[] = [];

    for (const arquivo of Array.from(lista)) {
      if (this.arquivos().length + aceitos.length >= LIMITE_ANEXOS_COMPLEMENTO) {
        recusas.push(`O complemento aceita no máximo ${LIMITE_ANEXOS_COMPLEMENTO} anexos.`);
        break;
      }

      const recusa = conferirArquivo(arquivo, FORMATOS_ANEXO_COMPLEMENTO, TETO_ANEXO_COMPLEMENTO_BYTES);

      if (recusa) {
        recusas.push(recusa);
        continue;
      }

      aceitos.push({ id: this.proximoId++, arquivo, tamanho: formatarTamanho(arquivo.size) });
    }

    this.arquivos.update((atuais) => [...atuais, ...aceitos]);
    this.recusas.set(recusas);
  }
}
